/**
 * Canvas-rendered gear icon for the persistent in-game overlay trigger.
 *
 * Each app's `OverlayRenderer` calls `drawGearIcon` to place the icon and uses
 * the returned bounds for hit-testing via `gearHitTest`. The icon is
 * theme-aware via passed color tokens — no hardcoded palette.
 */
export interface GearIconPalette {
  /** Body fill. */
  readonly fill: string;
  /** Border + tooth outline. */
  readonly border: string;
}

export interface GearIconBounds {
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

export interface GearIconOptions {
  readonly hover?: boolean;
}

const TOOTH_COUNT = 8;
const OUTER_RATIO = 0.42;
const INNER_RATIO = 0.30;
const HUB_RATIO = 0.16;
const TOOTH_HALF_ANGLE = Math.PI / TOOTH_COUNT / 2;

/**
 * Draw a gear icon centered in `[x, x+size] × [y, y+size]`. Returns the bounds
 * unchanged so callers can stash them for `gearHitTest`.
 */
export function drawGearIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  palette: GearIconPalette,
  opts: GearIconOptions = {},
): GearIconBounds {
  const cx = x + size / 2;
  const cy = y + size / 2;
  const rOuter = size * OUTER_RATIO;
  const rInner = size * INNER_RATIO;
  const rHub = size * HUB_RATIO;
  const hovered = opts.hover === true;

  ctx.save();
  ctx.fillStyle = palette.fill;
  ctx.strokeStyle = palette.border;
  ctx.lineWidth = Math.max(1, size * 0.04);
  if (hovered) {
    ctx.shadowColor = palette.border;
    ctx.shadowBlur = size * 0.18;
  }

  // Toothed body — alternates outer/inner radius vertices around the circle.
  ctx.beginPath();
  for (let i = 0; i < TOOTH_COUNT; i += 1) {
    const center = (i / TOOTH_COUNT) * Math.PI * 2 - Math.PI / 2;
    const t1 = center - TOOTH_HALF_ANGLE;
    const t2 = center + TOOTH_HALF_ANGLE;
    const t3 = center + Math.PI / TOOTH_COUNT - TOOTH_HALF_ANGLE;
    const t4 = center + Math.PI / TOOTH_COUNT + TOOTH_HALF_ANGLE;
    if (i === 0) {
      ctx.moveTo(cx + rOuter * Math.cos(t1), cy + rOuter * Math.sin(t1));
    } else {
      ctx.lineTo(cx + rOuter * Math.cos(t1), cy + rOuter * Math.sin(t1));
    }
    ctx.lineTo(cx + rOuter * Math.cos(t2), cy + rOuter * Math.sin(t2));
    ctx.lineTo(cx + rInner * Math.cos(t3), cy + rInner * Math.sin(t3));
    ctx.lineTo(cx + rInner * Math.cos(t4), cy + rInner * Math.sin(t4));
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Hub hole — punch through with an over-painted circle in the bg color is
  // brittle across themes, so draw a filled hub then border it.
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.arc(cx, cy, rHub, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fillStyle = palette.border;
  ctx.fill();

  ctx.restore();

  return { x, y, size };
}

/** The pause gear is the same size, the same distance from the corner, in every game. */
export const PAUSE_GEAR_SIZE = 28;
export const PAUSE_GEAR_MARGIN = 8;

export interface PauseGearOptions extends GearIconOptions {
  /** Distance from the right edge, for a board whose frame crowds the corner. Default: the margin. */
  readonly insetX?: number;
}

/** Where the pause gear sits in an area `areaWidth` wide: its top-right corner. */
export function pauseGearBounds(areaWidth: number, insetX: number = PAUSE_GEAR_MARGIN): GearIconBounds {
  return { x: areaWidth - PAUSE_GEAR_SIZE - insetX, y: PAUSE_GEAR_MARGIN, size: PAUSE_GEAR_SIZE };
}

/**
 * Draw the pause gear in the top-right corner of an area `areaWidth` wide —
 * the one way every game places it. Returns its bounds for `gearHitTest`.
 */
export function drawPauseGear(
  ctx: CanvasRenderingContext2D,
  areaWidth: number,
  palette: GearIconPalette,
  opts: PauseGearOptions = {},
): GearIconBounds {
  const { x, y, size } = pauseGearBounds(areaWidth, opts.insetX);
  return drawGearIcon(ctx, x, y, size, palette, opts);
}

/** Returns true when `(px, py)` is inside the gear icon bounds. */
export function gearHitTest(point: { x: number; y: number }, bounds: GearIconBounds): boolean {
  return (
    point.x >= bounds.x &&
    point.x <= bounds.x + bounds.size &&
    point.y >= bounds.y &&
    point.y <= bounds.y + bounds.size
  );
}
